import { access } from "fs";
import { createContext, useContext, useMemo, useReducer } from "react";
import { Customer, Order } from "../API";
import { OrderInfoForm } from "../components/StepperContent/types";
import { CreateStripePaymentIntentMutationResult } from "../DAL/repositories/PaymentRepository";
import { UploadImage } from "../util/Storage";

export type ApplicationStore = {
  customer: Customer | null;
  uploadImage: UploadImage | null;
  orderInfoForm: OrderInfoForm | null;
  stepperIndex: number;
  paymentInfo: CreateStripePaymentIntentMutationResult | null;
  shippingFee: number;
  redirectTo: string;
};

export enum ResourceOperation {
  FETCH_ORDER,
  FETCH_CUSTOMER,
  GET_ORDER,
  GET_CUSTOMER,
  CREATE_ORDER,
  CREATE_CUSTOMER,
  UPDATE_ORDER,
  UPDATE_CUSTOMER,
  UPLOAD_IMAGE,
  VALIDATED_ORDER_INFO_FORM,
  VALIDATED_CUSTOMER,
  ADD_INDEX,
  SUB_INDEX,
  CREATE_PAYMENT_INFO,
  CALC_SHIPPING_FEE,
  SET_REDIRECT,
}

export type ACTIONTYPE =
  | { type: ResourceOperation.FETCH_ORDER; payload: Order[] }
  | { type: ResourceOperation.FETCH_CUSTOMER; payload: Customer[] }
  | { type: ResourceOperation.GET_ORDER; payload: Order }
  | { type: ResourceOperation.GET_CUSTOMER; payload: Customer }
  | { type: ResourceOperation.CREATE_ORDER; payload: Order }
  | { type: ResourceOperation.CREATE_CUSTOMER; payload: Customer }
  | { type: ResourceOperation.UPDATE_ORDER; payload: Order }
  | { type: ResourceOperation.UPDATE_CUSTOMER; payload: Customer }
  | { type: ResourceOperation.UPLOAD_IMAGE; payload: UploadImage }
  | {
      type: ResourceOperation.VALIDATED_ORDER_INFO_FORM;
      payload: OrderInfoForm;
    }
  | { type: ResourceOperation.VALIDATED_CUSTOMER; payload: Customer }
  | { type: ResourceOperation.ADD_INDEX }
  | { type: ResourceOperation.SUB_INDEX }
  | { type: ResourceOperation.CALC_SHIPPING_FEE; payload: number }
  | {
      type: ResourceOperation.CREATE_PAYMENT_INFO;
      payload: CreateStripePaymentIntentMutationResult;
    }
  | { type: ResourceOperation.SET_REDIRECT; payload: string };

export const ApplicationStoreContext = createContext(
  {} as {
    state: ApplicationStore;
    dispatch: React.Dispatch<ACTIONTYPE>;
  }
);

const reducer = (state: ApplicationStore, action: ACTIONTYPE) => {
  switch (action.type) {
    case ResourceOperation.FETCH_CUSTOMER:
      return state;
    case ResourceOperation.FETCH_ORDER:
      return state;
    case ResourceOperation.GET_CUSTOMER:
      return { ...state, customer: action.payload };
    case ResourceOperation.GET_ORDER:
      return state;
    case ResourceOperation.CREATE_ORDER:
      return state;
    case ResourceOperation.CREATE_CUSTOMER:
      return { ...state, customer: action.payload };
    case ResourceOperation.UPDATE_ORDER:
      return state;
    case ResourceOperation.UPDATE_CUSTOMER:
      return { ...state, customer: action.payload };
    case ResourceOperation.UPLOAD_IMAGE:
      console.info(action.payload);
      return { ...state, uploadImage: action.payload };
    case ResourceOperation.VALIDATED_ORDER_INFO_FORM:
      console.debug(action.payload);
      return { ...state, orderInfoForm: action.payload };
    case ResourceOperation.VALIDATED_CUSTOMER:
      console.debug(action.payload);
      return { ...state, customer: action.payload };
    case ResourceOperation.ADD_INDEX:
      console.log({ ...state, stepperIndex: state.stepperIndex + 1 });
      return { ...state, stepperIndex: state.stepperIndex + 1 };
    case ResourceOperation.SUB_INDEX:
      return { ...state, stepperIndex: state.stepperIndex - 1 };
    case ResourceOperation.CREATE_PAYMENT_INFO:
      return { ...state, paymentInfo: action.payload };
    case ResourceOperation.CALC_SHIPPING_FEE:
      return { ...state, shippingFee: action.payload };
    case ResourceOperation.SET_REDIRECT:
      console.info('redicetTo:',action.payload)
      return {...state, redirectTo: action.payload}
    default:
      return state;
  }
};

const initialStore: ApplicationStore = {
  customer: null,
  uploadImage: null,
  orderInfoForm: null,
  stepperIndex: 0,
  paymentInfo: null,
  shippingFee: 0,
  redirectTo: "cookie",
};

type Props = {
  children?: React.ReactNode;
};

export const ApplicationStoreProvider: React.FC<Props> = (props) => {
  const [state, dispatch] = useReducer(reducer, initialStore);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <ApplicationStoreContext.Provider value={value} {...props} />;
};

export const useApplicationStore = () => {
  const context = useContext(ApplicationStoreContext);
  if (typeof context === "undefined") {
    throw new Error(
      "useApplicationStore must be within a ApplicationStoreProvider."
    );
  }
  return context;
};
